import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Color } from 'src/app/shared/types';
import { HapticFeedbackDirective } from 'src/app/core/directives';

/** Valid size variants for the chip, ordered smallest to largest. */
export type ChipSize = 'xs' | 'sm' | 'md' | 'lg';

/** Tailwind class strings per chip size. Uses em units so padding scales with font-size. */
const SIZE_CLASSES: Record<ChipSize, string> = {
  xs: 'text-[0.65rem] px-[0.5em] py-[0.125em]',
  sm: 'text-sm px-[0.625em] py-1.5',
  md: 'text-[0.9rem] px-[0.75em] py-[0.25em]',
  lg: 'text-base px-[0.875em] py-[0.375em]',
};

/** Class list applied to every chip regardless of variant. */
const BASE_CLASSES =
  'chip-container inline-flex items-center justify-center gap-x-1 max-w-full ' +
  'select-none font-semibold leading-[1.5] text-center text-ellipsis overflow-hidden whitespace-nowrap ' +
  'focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]';

/** Semantic disabled treatment. Overrides all color variants — disabled chips look identical. */
const DISABLED_CLASSES = 'bg-surface-disabled text-disabled border border-outline';

/**
 * Compact chip component for tags, labels, status indicators, filter pills, or removable selections.
 *
 * The chip has four rendering paths, evaluated in this order:
 *
 * 1. **Disabled** — semantic disabled tokens override everything. All disabled chips
 *    look identical regardless of color, for consistent UX and WCAG compliance.
 * 2. **`customClass`** — escape hatch for one-off styling. Size and shape still apply.
 * 3. **`outline`** — transparent background with `border-{color}` + `text-{color}`.
 * 4. **Solid fill (default)** — `bg-{color}` with automatic contrast text + hover shade.
 *
 * Clickable chips are fully keyboard-accessible: tab-focusable, `role="button"`,
 * activate on Enter or Space, and emit haptic feedback on tap. Removable chips expose
 * a trailing close button and also emit `removed` on Backspace/Delete while focused,
 * matching standard Material / Ionic chip behavior. Disabled chips are removed from
 * the tab order and cannot be activated or dismissed.
 *
 * ```html
 * <!-- Static neutral chip (default) -->
 * <app-chip>Default</app-chip>
 *
 * <!-- Status chip -->
 * <app-chip color="success" size="sm">Active</app-chip>
 *
 * <!-- Outline chip -->
 * <app-chip color="purple" [outline]="true">Outline</app-chip>
 *
 * <!-- Clickable filter chip with haptic feedback and a11y label -->
 * <app-chip
 *   color="purple"
 *   [clickable]="true"
 *   ariaLabel="Toggle 'Reading' filter"
 *   (click)="onToggle('reading')"
 * >
 *   Reading
 * </app-chip>
 *
 * <!-- Removable tag (trailing × button, Backspace/Delete also fire removed) -->
 * <app-chip
 *   color="purple"
 *   [removable]="true"
 *   removeAriaLabel="Remove 'Reading' tag"
 *   (removed)="onRemove('reading')"
 * >
 *   Reading
 * </app-chip>
 *
 * <!-- Both clickable and removable (body and × are independent targets) -->
 * <app-chip
 *   color="aqua"
 *   [clickable]="true"
 *   [removable]="true"
 *   (click)="onSelect('math')"
 *   (removed)="onRemove('math')"
 * >
 *   Math
 * </app-chip>
 *
 * <!-- Disabled clickable chip (skipped by Tab, semantic disabled styling) -->
 * <app-chip color="danger" [clickable]="true" [disabled]="true">Locked</app-chip>
 *
 * <!-- Fully custom styling (color and outline are ignored) -->
 * <app-chip customClass="bg-gradient-to-r from-coral to-amber text-white">Custom</app-chip>
 * ```
 *
 * @feature shared
 */
@Component({
  selector: 'app-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HapticFeedbackDirective],
  host: {
    class: 'inline-block',
    '[class.cursor-pointer]': 'interactive()',
    '[class.cursor-not-allowed]': '(clickable() || removable()) && disabled()',
    '[class.pointer-events-none]': 'disabled()',
  },
  template: `
    <div
      #chip
      [class]="classes()"
      [attr.role]="clickable() ? 'button' : null"
      [attr.tabindex]="focusable() ? 0 : null"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-disabled]="disabled() || null"
      (keydown.enter)="onKeyActivate($event, chip)"
      (keydown.space)="onKeyActivate($event, chip)"
      (keydown.backspace)="onKeyRemove($event)"
      (keydown.delete)="onKeyRemove($event)"
      appHapticFeedback
      [disabled]="!interactive()"
    >
      <ng-content />
      @if (removable()) {
        <button
          type="button"
          class="chip-remove appearance-none inline-flex items-center justify-center rounded-full! aspect-square
                 -mr-[0.25em] ml-[0.125em] w-[1.4em] h-[1.4em] shrink-0 border-0 p-0
                 text-current cursor-pointer leading-none [font-size:inherit]
                 bg-[var(--theme-state-hover)]
                 hover:bg-[var(--theme-state-press)] focus-visible:bg-[var(--theme-state-press)]
                 hover:scale-110 active:scale-95
                 focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]
                 transition-all duration-150"
          [attr.aria-label]="removeAriaLabel()"
          [attr.tabindex]="disabled() ? -1 : 0"
          [disabled]="disabled() || null"
          (click)="onRemoveClick($event)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="w-[0.875em] h-[0.875em]"
            aria-hidden="true"
            focusable="false"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      } @else if (removeHint()) {
        <span
          class="inline-flex items-center justify-center -mr-[0.125em] ml-[0.25em] w-[1em] h-[1em] shrink-0
                 text-danger pointer-events-none [font-size:inherit]"
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="w-[0.875em] h-[0.875em]"
            aria-hidden="true"
            focusable="false"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </span>
      }
    </div>
  `,
})
export class ChipComponent {
  // --- Appearance ---

  /**
   * Ionic color token applied to the chip.
   * - Default rendering: solid `bg-{color}` fill with automatic contrast text.
   * - With `outline=true`: used for `border-{color}` and `text-{color}`.
   * - Ignored when `disabled` or `customClass` is set.
   * @default 'medium'
   */
  readonly color = input<Color>('medium');

  /** Chip size variant. @default 'sm' */
  readonly size = input<ChipSize>('sm');

  /** Renders with colored border and text only, no background fill. @default false */
  readonly outline = input(false);

  /**
   * Renders as a fully rounded pill. When `false`, renders with slightly rounded corners.
   * @default true
   */
  readonly pill = input(true);

  /** Adds a soft theme drop shadow. @default false */
  readonly shadow = input(false);

  /**
   * Escape-hatch Tailwind class string. When set, `color` and `outline` are ignored
   * and the chip renders only the supplied classes alongside size and shape. Disabled
   * state still wins over this.
   * @default undefined
   */
  readonly customClass = input<string | undefined>(undefined);

  // --- Behavior ---

  /**
   * Enables click interactions on the chip body. Sets `role="button"`, makes the chip
   * a tab stop, handles Enter/Space keyboard activation, and triggers haptic feedback
   * on tap.
   * @default false
   */
  readonly clickable = input(false);

  /**
   * Renders a trailing close (×) button that emits `(removed)` when activated. The chip
   * body itself also emits `(removed)` on Backspace or Delete while focused, matching
   * standard Material / Ionic chip behavior. Works independently of `clickable`: a chip
   * can be removable without the body being clickable, and vice versa.
   * @default false
   */
  readonly removable = input(false);

  /**
   * Renders a non-interactive red × at the trailing end to signal that clicking the chip
   * dismisses it. The icon is purely decorative — click handling lives on the chip body
   * via `clickable`. Ignored when `removable` is true (the real × button takes precedence).
   * @default false
   */
  readonly removeHint = input(false);

  /**
   * Disables the chip. Replaces the variant with semantic disabled tokens
   * (`bg-surface-disabled`, `text-disabled`), sets `aria-disabled="true"`, removes
   * the chip from the tab order, blocks pointer events, and switches the cursor
   * to `not-allowed`.
   * @default false
   */
  readonly disabled = input(false);

  /**
   * Whether the chip body itself participates in the tab order. Set to `false`
   * for "tag-input" style chip lists where only the trailing × should be tab-
   * focusable — the chip body is decorative and removal happens via the
   * dedicated remove button. Has no effect on the × button's own tab order.
   * @default true
   */
  readonly bodyFocusable = input(true);

  // --- Accessibility ---

  /**
   * Accessible label for the chip body announced by screen readers. Required for
   * clickable chips that have no visible text, and recommended for clickable chips
   * whose text alone is ambiguous.
   */
  readonly ariaLabel = input<string | undefined>(undefined);

  /**
   * Accessible label for the trailing remove button. Should describe *what* will be
   * removed (e.g. "Remove 'Reading' tag") rather than just "Remove".
   * @default 'Remove'
   */
  readonly removeAriaLabel = input<string>('Remove');

  // --- Outputs ---

  /**
   * Fired when the chip is dismissed via the trailing × button, or via Backspace /
   * Delete keyboard shortcut while the chip body is focused. Not fired when disabled.
   */
  readonly removed = output<void>();

  // --- Derived state (computed signals) ---

  /** True when the chip body should respond to pointer and keyboard click input. */
  readonly interactive = computed(() => this.clickable() && !this.disabled());

  /**
   * True when the chip body should be in the tab order. Removable-only chips are
   * focusable so keyboard users can trigger Backspace/Delete dismissal, even though
   * they don't get `role="button"`. Suppressed when `bodyFocusable` is false.
   */
  readonly focusable = computed(
    () => this.bodyFocusable() && (this.clickable() || this.removable()) && !this.disabled(),
  );

  /** Resolved Tailwind class string for the chip's inner container element. */
  readonly classes = computed(() => {
    const parts: string[] = [BASE_CLASSES, SIZE_CLASSES[this.size()], this.pill() ? 'rounded-full' : 'rounded'];

    if (this.shadow()) {
      parts.push('theme-shadow');
    }
    // `clickable` CSS class activates the `.bg-{color}.clickable:hover` shade rule
    // from _color-classes.scss. Only add it when the chip body is actually interactive.
    if (this.interactive()) {
      parts.push('clickable');
    }

    // Rendering path precedence: disabled > customClass > outline > solid fill.
    if (this.disabled()) {
      parts.push(DISABLED_CLASSES);
    } else if (this.customClass()) {
      parts.push(this.customClass() as string);
    } else if (this.outline()) {
      const color = this.color();
      parts.push('bg-transparent', 'border', `border-${color}`, `text-${color}`);
    } else {
      parts.push(`bg-${this.color()}`);
    }

    return parts.join(' ');
  });

  // --- Event handlers ---

  /**
   * Forwards Enter/Space keydown events on the chip body as a synthetic click so any
   * `(click)` listener on the host fires identically for keyboard and pointer input.
   * No-op when the chip is not interactive.
   *
   * `event` is typed as `Event` because Angular's key-alias bindings
   * (`keydown.enter`, `keydown.space`) infer `$event` as the base `Event` type.
   */
  onKeyActivate(event: Event, chip: HTMLElement): void {
    if (!this.interactive()) {
      return;
    }
    event.preventDefault();
    chip.click();
  }

  /**
   * Emits `removed` when Backspace or Delete is pressed on a removable, enabled chip.
   * Matches the standard Material chip dismissal keyboard shortcut.
   */
  onKeyRemove(event: Event): void {
    if (!this.removable() || this.disabled()) {
      return;
    }
    event.preventDefault();
    this.removed.emit();
  }

  /**
   * Handles activation of the trailing × button. Stops propagation so the chip body's
   * own `(click)` listener does not fire for removable + clickable chips.
   */
  onRemoveClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.disabled()) {
      return;
    }
    this.removed.emit();
  }
}
